import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocalStorage } from 'react-use'
import { v4 as uuidv4 } from 'uuid'

import { PageType } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ITableRow } from '@makyoapp/makyo-sdk/models/Table.js'
import { ColumnAdapter } from '@makyoapp/makyo-common'

import { DEFAULT_LANGUAGE } from '@/core/db'
import constate from '@/lib/constate'
import cookies from '@/lib/cookies'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAuthPlugin } from '@/hooks/useAuthPlugin'
import { STORAGE_KEYS } from '@/shared/constants'
import { EVENT_KEYS } from '@/shared/custom-event'

import { useAppBuilderContext } from '../AppBuilderContext'
import { useAppClientContext } from './AppClientContext'

export interface AppAuthState {
  id: string
  email: string
  role?: string
  accessToken?: string
  refreshToken?: string
}

export interface AppAuthUser {
  id: string
  email: string
  name: string
  photo: string
  role?: string
}

export type AuthLoginArgs = {
  payload: { step: number; data: Record<string, string> }
  onNextStep: () => void | Promise<void>
  onFinish: () => void | Promise<void>
  onError: (err: any) => void | Promise<void>
  onFinally: () => void | Promise<void>
}

/**
 * Auth context for app builder
 */
const [AppAuthProvider, useAppAuthContext] = constate('AppAuthContext', () => {
  const { appClient } = useAppClientContext()
  const { dbWorker, tablesMap, userTable, sessionTable } = useDbClientContext()
  const { project, pages, updateProjectPrivacy } = useAppBuilderContext()

  // ========================= auth plugin =========================
  const { authPlugin, authForm, isLoadingAuthPlugin } = useAuthPlugin()
  const activePluginId = authPlugin?._id || ''

  // ========================= privacy redirect pages =========================
  const appLoginPage = pages?.find((p) => p.type === PageType.Login)

  const privacySettings = project?.privacy
  const privacyUserTableId = privacySettings?._UsersTableID
  const projectAuthPluginId = privacySettings?._AuthPlugin._PluginID
  const postLoginRedirectPageId = privacySettings?._PostAuthenticatePageID
  const postLogoutRedirectPageId = privacySettings?._PostLogoutPageID
  const postUnauthenticatedRedirectPageId = privacySettings?._PostUnauthenticatedPageID

  const postLoginPage = pages?.find((p) => p.id === postLoginRedirectPageId) ?? appLoginPage
  const postLogoutPage = pages?.find((p) => p.id === postLogoutRedirectPageId) ?? appLoginPage
  const postUnauthenticatedPage = pages?.find((p) => p.id === postUnauthenticatedRedirectPageId) ?? appLoginPage
  const postUnauthenticatedPageSlug = postUnauthenticatedPage?.slug
  const defaultRedirectPageId = postLoginPage?.id || ''

  // ========================= state =========================
  const [tableUsers, setTableUsers] = useState<AppAuthUser[]>([])
  const [authState, setAuthState] = useLocalStorage<AppAuthState | null>(
    `${STORAGE_KEYS.APP_USER_AUTH}:${window.appID}`,
    null
  )
  const [loadingUserTable, setLoadingUserTable] = useState(true)
  const [loadingSessionTable, setLoadingSessionTable] = useState(true)
  const isUserLoggedInRef = useRef(!!authState)
  const tempToken = useRef<string>('')
  const sessionValidatedRef = useRef<string>('')
  const hasPrefetchBeforeLoginRef = useRef(false)

  // get user table client from privacy settings or default user table
  const userTableClient = useMemo(() => {
    const defaultUserTableId = privacyUserTableId || userTable?.id || ''
    return tablesMap.get(defaultUserTableId)
  }, [privacyUserTableId, tablesMap, userTable?.id])

  const defaultUserTableColumns = useMemo(() => {
    const defaultUserTable = Array.from(tablesMap.values()).find((tb) => tb.isUserTable)
    const usersTableColumns = defaultUserTable?.GetUserTableColumnAttributes()
    if (!defaultUserTable || !usersTableColumns) return
    return {
      email: usersTableColumns.email?._id || '',
      name: usersTableColumns.name?._id || '',
      photo: usersTableColumns.photo?._id || '',
      role: usersTableColumns.role?._id || '',
      phone: usersTableColumns.phone?._id || '',
    }
  }, [tablesMap])

  const privacyUserTableColumns = useMemo(() => {
    if (!privacySettings) return
    const { _EmailColID, _NameColID, _PictureColID, _RoleColID, _PhoneColID } = privacySettings
    return {
      email: _EmailColID,
      name: _NameColID,
      photo: _PictureColID,
      role: _RoleColID,
      phone: _PhoneColID,
    }
  }, [privacySettings])

  // get user table & columns
  const userColumns = useMemo(() => {
    return privacyUserTableId ? privacyUserTableColumns : defaultUserTableColumns
  }, [defaultUserTableColumns, privacyUserTableColumns, privacyUserTableId])

  // generate session table & columns
  const sessionTableClient = sessionTable ? tablesMap.get(sessionTable.id) : undefined

  const sessionColumns = useMemo(() => {
    if (!sessionTable) return
    return sessionTable.columns.reduce<Record<string, ColumnAdapter>>((acc, col) => {
      if (col.name === 'Session ID') acc.sessionID = col
      if (col.name === 'Timestamp') acc.timestamp = col
      if (col.name === 'Email') acc.email = col
      return acc
    }, {})
  }, [sessionTable])

  // derived
  const userId = authState?.id || ''
  const userEmail = authState?.email || ''
  const isUserLoggedIn = isUserLoggedInRef.current
  const isAuthenticated = !!authState?.id && !!authState?.email
  const isLoading = loadingUserTable || isLoadingAuthPlugin

  const shouldPendingSessionTable = isLoading || !sessionTableClient || (!!sessionTableClient && loadingSessionTable)

  const currentUser = tableUsers.find((user) => user.id === userId)

  /**
   * handle request user table data
   */
  const handleRequestUserTableData = useCallback(() => {
    if (!dbWorker || !userTableClient) return

    dbWorker.sendAction({
      type: 'get_table_data',
      payload: {
        table_id: userTableClient.settings._id,
        language: DEFAULT_LANGUAGE,
      },
    })
  }, [dbWorker, userTableClient])

  /**
   * handle request session table data
   */
  const handleRequestSessionTableData = useCallback(() => {
    if (!dbWorker || !sessionTableClient) return

    dbWorker.sendAction({
      type: 'get_table_data',
      payload: {
        table_id: sessionTableClient.settings._id,
        language: DEFAULT_LANGUAGE,
      },
    })
  }, [dbWorker, sessionTableClient])

  // ========================= effects =========================

  // todo: this logic is only for newly created app
  // initialize auth plugin if its not existed in project privacy yet
  useEffect(() => {
    if (!privacySettings || !userTableClient || !userColumns) return

    if (activePluginId && projectAuthPluginId !== activePluginId) {
      updateProjectPrivacy({
        _PublicAcces: privacySettings._PublicAcces,
        _PostAuthenticatePageID: defaultRedirectPageId,
        _PostLogoutPageID: defaultRedirectPageId,
        _PostUnauthenticatedPageID: defaultRedirectPageId,
        _PluginID: activePluginId,
        _Backend: {
          'login-method': JSON.stringify({
            Value: 'valid-email',
            EditionValue: null,
          }),
        },
        _Frontend: {},
        _UsersTableID: userTableClient.settings._id,
        _EmailColID: userColumns.email || '',
        _NameColID: userColumns.name || '',
        _PictureColID: userColumns.photo || '',
        _RoleColID: userColumns.role || '',
        _PhoneColID: userColumns.phone || '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePluginId, projectAuthPluginId, defaultRedirectPageId])

  // get user table data
  useEffect(() => {
    if (!dbWorker) return

    let unsubs = []

    if (userTableClient && userColumns) {
      setLoadingUserTable(true)

      handleRequestUserTableData()

      const unsubscribe = userTableClient.subscribe(() => {
        const users = (userTableClient.GetTableUsers() || []).map((row) => {
          return {
            id: row._id,
            email: row[userColumns?.email]?.toString() || '',
            name: row[userColumns?.name]?.toString() || '',
            photo: row[userColumns?.photo]?.toString() || '',
            role: row[userColumns?.role]?.toString() || '',
          }
        })
        setTableUsers(users)
        setLoadingUserTable(false)
      })

      unsubs.push(unsubscribe)
    }

    if (sessionTableClient) {
      setLoadingSessionTable(true)

      handleRequestSessionTableData()

      const unsubscribeSession = sessionTableClient.subscribe(() => {
        setLoadingSessionTable(false)
      })

      unsubs.push(unsubscribeSession)
    }

    return () => {
      unsubs.forEach((unsub) => unsub())
    }
  }, [
    dbWorker,
    handleRequestSessionTableData,
    handleRequestUserTableData,
    sessionTableClient,
    userColumns,
    userTableClient,
  ])

  // check if user is in userTable
  useEffect(() => {
    if (!tableUsers.length || !authState) return

    // check current logged-in user
    if (authState.email) {
      // check if user is in userTable
      const tableUser = tableUsers.find((user) => user.email === authState.email)

      if (!tableUser) {
        let currentUser: AppAuthUser = {
          id: authState.id,
          email: authState.email,
          role: authState.role,
          name: '',
          photo: '',
        }

        // add user to userTable
        setTableUsers([...tableUsers, currentUser])
      }
    }
  }, [authState, tableUsers])

  // validate session
  useEffect(() => {
    if (shouldPendingSessionTable || !sessionColumns) return

    const currentSessionCookie = cookies.getAppSessionId(window.appID)

    const { sessionID, email, timestamp } = sessionColumns
    const sessionColId = sessionID.id
    const emailColId = email.id
    const timestampColId = timestamp.id

    const sessionsData = sessionTableClient.GetDbWorkerRows(DEFAULT_LANGUAGE, '')

    // Create a unique key to track if we've already processed this state
    const validationKey = `${authState?.id || 'anonymous'}-${currentSessionCookie || 'no-cookie'}-${authState?.email || 'no-email'}`

    // Prevent infinite re-renders by checking if we've already processed this exact state
    if (sessionValidatedRef.current === validationKey) {
      return
    }

    if (!!authState) {
      // find current user in session table
      const currentSessionRow = sessionsData?.find((s) => s[sessionColId] === currentSessionCookie)

      if (!!currentSessionRow) {
        // Session exists in table
        if (!currentSessionRow[emailColId]) {
          console.log('update session email and timestamp')
          // update session email and timestamp
          sessionTableClient.UpdateData([
            {
              ...currentSessionRow,
              [timestampColId]: new Date().toISOString(),
              [emailColId]: authState.email,
            } as ITableRow,
          ])
        } else if (currentSessionRow[emailColId] === authState.email) {
          // Session already exists with correct email, just mark as validated
          console.log('session already valid for current user')
        } else {
          console.log('session exists but for different user, create new session')
          // Session exists but for different user, create new session
          const newSessionId = uuidv4()
          const newSession = {
            [sessionColId]: newSessionId,
            [emailColId]: authState.email,
            [timestampColId]: new Date().toISOString(),
          } as ITableRow

          sessionTableClient.AddRow(newSession)
          cookies.setAppSessionId(window.appID, newSessionId)
        }
        sessionValidatedRef.current = validationKey
      } else {
        console.log('create new session for user - no existing session found')
        // create new session if not exist
        const newSessionId = uuidv4()
        const newSession = {
          [sessionColId]: newSessionId,
          [emailColId]: authState.email,
          [timestampColId]: new Date().toISOString(),
        } as ITableRow

        // add new session to sessionTable
        sessionTableClient.AddRow(newSession)
        cookies.setAppSessionId(window.appID, newSessionId)
        sessionValidatedRef.current = validationKey
      }
    } else {
      // check if there is a session id in cookies
      if (!!currentSessionCookie) {
        const userSessionRow = sessionsData?.find((s) => s[sessionColId] === currentSessionCookie)
        if (!!userSessionRow) {
          sessionValidatedRef.current = validationKey
          return
        }
      }
      // create new session
      const newSessionId = uuidv4()
      const newSession = {
        [sessionColId]: newSessionId,
        [timestampColId]: new Date().toISOString(),
      } as ITableRow
      // add new session to sessionTable
      sessionTableClient.AddRow(newSession)
      cookies.setAppSessionId(window.appID, newSessionId)
      sessionValidatedRef.current = validationKey
    }
  }, [authState, sessionColumns, sessionTableClient, shouldPendingSessionTable])

  // reset state when user table updated
  useEffect(() => {
    const handleResetState = () => {
      // reload app builder
      window.dispatchEvent(new CustomEvent(EVENT_KEYS.APP_CLIENT_RELOAD))
      setAuthState(null)
    }
    window.addEventListener(EVENT_KEYS.APP_AUTH_USER_TABLE_UPDATED, handleResetState)
    return () => {
      window.removeEventListener(EVENT_KEYS.APP_AUTH_USER_TABLE_UPDATED, handleResetState)
    }
  }, [setAuthState, userTableClient])

  // ========================= methods =========================

  /**
   * Clean up session
   * This function will retain the session id but clear the email field
   */
  const cleanUpSession = useCallback(() => {
    if (!sessionColumns || !sessionTableClient) return

    const currentSessionCookie = cookies.getAppSessionId(window.appID)
    const { sessionID, email } = sessionColumns

    if (!currentSessionCookie || !sessionID || !email) return

    const sessionColId = sessionID.id
    const emailColId = email.id

    // Find the session row for the current session ID
    const sessionsData = sessionTableClient.GetDbWorkerRows(DEFAULT_LANGUAGE, '')
    const currentSessionRow = sessionsData?.find((s) => s[sessionColId] === currentSessionCookie)
    if (currentSessionRow) {
      // Clear the email field
      sessionTableClient.UpdateData([
        {
          ...currentSessionRow,
          [emailColId]: '',
        } as ITableRow,
      ])
    }

    // Do NOT remove the session id from cookies
    sessionValidatedRef.current = ''
  }, [sessionColumns, sessionTableClient])

  /**
   * handle logout
   */
  const handleLogout = useCallback(() => {
    setAuthState(null)
    isUserLoggedInRef.current = false
    cleanUpSession()
    // * auto redirect is disabled for now, will prioritize the action instead
    // goToPage({ slug: postUnauthenticatedPageSlug })
  }, [setAuthState, cleanUpSession])

  const handleUpdateSessionByEmail = useCallback(
    (email: string) => {
      const currentSessionCookie = cookies.getAppSessionId(window.appID)
      if (!sessionColumns || !sessionTableClient || !currentSessionCookie) return

      const sessionColId = sessionColumns?.sessionID?.id || ''
      const emailColId = sessionColumns?.email?.id || ''
      const sessionsData = sessionTableClient?.GetDbWorkerRows(DEFAULT_LANGUAGE, '')
      const currentSessionRow = sessionsData?.find((s) => s[sessionColId] === currentSessionCookie)

      sessionTableClient?.UpdateData([
        {
          ...currentSessionRow,
          [emailColId]: email,
        } as ITableRow,
      ])
    },
    [sessionColumns, sessionTableClient]
  )

  /**
   * handle login user
   */
  const handleLoginUser = useCallback(
    (id: string | null, accessToken?: string, refreshToken?: string) => {
      // get current user
      const currentUser = tableUsers.find((user) => user.id === id)

      if (!id || !currentUser) {
        handleUpdateSessionByEmail('')
        setAuthState(null)
        isUserLoggedInRef.current = false
        return
      }

      // Update session row with new user's email
      handleUpdateSessionByEmail(currentUser.email)

      // set auth state
      setAuthState({
        id,
        email: currentUser.email,
        role: currentUser.role,
        accessToken,
        refreshToken,
      })
      isUserLoggedInRef.current = true
    },
    [setAuthState, tableUsers, handleUpdateSessionByEmail]
  )

  /**
   * handle register user
   */
  const handleRegisterUser = useCallback(
    async (userData: Partial<AppAuthUser>, accessToken: string, refreshToken: string) => {
      if (!userTableClient || !userColumns) return

      // format the payload (row to add)
      const payload = {
        [userColumns.email]: userData.email,
        [userColumns.name]: userData.name,
        [userColumns.photo]: userData.photo,
        [userColumns.role]: userData.role,
      }

      // add to userTable
      const userRow = await userTableClient?.AddRow(payload as any)
      if (!userRow) return

      const newUser: AppAuthUser = {
        id: userRow._id,
        email: userRow[userColumns.email]?.toString() || '',
        name: userRow[userColumns.name]?.toString() || '',
        photo: userRow[userColumns.photo]?.toString() || '',
        role: userRow[userColumns.role]?.toString() || '',
      }

      setTableUsers([...tableUsers, newUser])

      // set auth state
      setAuthState({
        id: newUser.id,
        email: newUser.email,
        role: newUser.role,
        accessToken,
        refreshToken,
      })
      isUserLoggedInRef.current = true

      return newUser
    },
    [setAuthState, tableUsers, userColumns, userTableClient]
  )

  /**
   * handle auth login
   */
  const handleAuthLogin = useCallback(
    async ({ payload, onNextStep, onFinish, onError, onFinally }: AuthLoginArgs) => {
      if (!appClient) return

      if (!hasPrefetchBeforeLoginRef.current) {
        handleRequestUserTableData()
        hasPrefetchBeforeLoginRef.current = true
      }

      try {
        // inject token if exist
        if (tempToken.current) {
          payload.data['token'] = tempToken.current
        }

        // authenticate user
        const res = await appClient.provider.authenticateApp(window.appID, {
          _Step: payload.step,
          _Form: payload.data,
        })

        // store current token if any
        if (res.AccessToken) {
          tempToken.current = res.AccessToken
        }

        if (res._HasNextStep) {
          await onNextStep()
        } else {
          let user = tableUsers.find((u) => u.email.toLowerCase() === res.User.Email.toLowerCase())

          // if user not found, add user to auth context
          if (!user) {
            user = await handleRegisterUser(
              {
                email: res.User.Email,
                name: res.User.Name,
                photo: res.User.Photo,
                role: 'user',
              },
              res.AccessToken,
              res.RefreshToken
            )
          } else {
            handleLoginUser(user.id, res.AccessToken, res.RefreshToken)
          }

          if (onFinish) {
            await onFinish()
          }
          hasPrefetchBeforeLoginRef.current = false
        }
        //
      } catch (err) {
        onError(err)
      } finally {
        onFinally()
      }
    },
    [appClient, tableUsers, handleRegisterUser, handleLoginUser, handleRequestUserTableData]
  )

  return {
    authState,
    userId,
    userEmail,
    handleLoginUser,
    handleRegisterUser,
    tableUsers,
    currentUser,
    isUserLoggedIn,
    isAuthenticated,
    handleLogout,
    isLoading,

    // Auth Plugin Setting
    authPlugin,
    authForm,
    postLoginPage,
    postLogoutPage,
    postUnauthenticatedPage,
    postUnauthenticatedPageSlug,

    // methods
    handleAuthLogin,
  }
})

export { AppAuthProvider, useAppAuthContext }
